import { HttpErrorResponse } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnInit,
  Renderer2,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { ActivatedRoute, Data, Router } from '@angular/router';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatIcon } from '@angular/material/icon';
import { MatLegacyAutocomplete as MatAutocomplete } from '@angular/material/legacy-autocomplete';
import { MatLegacySelectChange as MatSelectChange } from '@angular/material/legacy-select';
import { CdkDragDrop, moveItemInArray, transferArrayItem } from '@angular/cdk/drag-drop';

import moment from 'moment';
import { OnDestroyMixin, untilComponentDestroyed } from '@w11k/ngx-componentdestroyed';
import { ReportLayout, DaysOfWeek, ReportLayoutDefinition } from 'app/modules/reports/reports.types';
import { AccountService } from 'app/core';
import { User } from 'app/core/models/user.types';
import { Account, FieldDef } from 'app/core/models/account.types';
import { ReportsService } from 'app/modules/reports/reports.service';
import { Project } from 'app/modules/projects/projects.types';
import { FileFormat, RelatedToType } from 'app/shared/enums';
import { camelToSnakeCase, FJSON } from 'app/shared/Utils/common.utils';
import { generateDownloadableFile, getAllowedFields } from 'app/shared/functions';
import { filter, map } from 'rxjs/operators';
import { ConfirmService } from 'app/shared/components/widgets/dialog/confirm/confirm.service';
import { TsSnackbarService } from 'tsui';
import { FeatureFlag, NewPermission } from '@core/enums';
import { Store } from '@ngxs/store';
import { UserState } from '@state/user/state';
import { AccountState } from '@state/account/state';
import { PERMISSION_DENY_LABEL } from '@shared/literals';

@Component({
  selector: 'reports-details',
  templateUrl: './details.component.html',
  styleUrls: ['./details.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReportsDetailsComponent extends OnDestroyMixin implements OnInit {
  editMode: boolean;
  tagsEditMode: boolean;
  foundTags: string[];
  reportLayout: ReportLayout<any>;
  contactForm: UntypedFormGroup;
  projects: Project[];
  account: Account;
  data: any;
  parsedDefinition: ReportLayoutDefinition;
  parsedFilters: any;
  fieldDefs: Array<FieldDef> = [];
  modifiedWithoutSave: boolean = false;
  reportLayoutForm: UntypedFormGroup;
  filtersForm: UntypedFormGroup;
  visible = true;
  runningReport = false;
  users: User[];
  isFilterRowsValid: boolean;
  PERMISSION_DENY_LABEL = PERMISSION_DENY_LABEL;
  private _internalFilterState: { fjson: string };
  protected readonly NewPermission = NewPermission;

  public headerAlignmentValues: Array<string> = ['center', 'left', 'right'];
  public colorPickerOptions = {
    defaultColor: '#0975a7',
    selectedColor: '',
    cpPosition: 'top-right',
    cpOKButton: true,
    cpCancelButton: true,
    cpToggle: false,
  };

  @ViewChild('tagInput') tagInput: ElementRef<HTMLInputElement>;
  @ViewChild('auto') matAutocomplete: MatAutocomplete;
  @ViewChild('logoFileInput', { static: true }) private logoFileInput: ElementRef;
  @ViewChild('cpIcon', { static: true }) private cpIcon: MatIcon;

  get hasReportFields(): boolean {
    return this.parsedDefinition.pipeline.fields?.length > 0;
  }

  get isFormValid(): boolean {
    return this.reportLayoutForm.get('name').valid;
  }

  get hasPermissionToEditForm(): boolean {
    return (
      this.store.selectSnapshot(UserState.hasPermission(NewPermission.PDF_REPORTS_EDIT)) &&
      this.reportLayout.permissions?.is_editable
    );
  }

  constructor(
    private readonly activatedRoute: ActivatedRoute,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly reportsService: ReportsService,
    private readonly formBuilder: UntypedFormBuilder,
    private readonly router: Router,
    private readonly accountService: AccountService,
    private readonly tsSnackbarService: TsSnackbarService,
    private readonly renderer: Renderer2,
    private readonly confirmService: ConfirmService,
    private readonly store: Store,
  ) {
    super();
  }

  ngOnInit(): void {
    this.account = this.accountService.getCurrentAccount();
    this.activatedRoute.data.pipe(untilComponentDestroyed(this)).subscribe((data: Data) => {
      this.data = data;
      this.fieldDefs = getAllowedFields(this.data.fieldDefs, RelatedToType.Project);
    });

    this.reportLayoutForm = this.formBuilder.group({
      name: ['', [Validators.required]],
      description: [''],
      column_color: this.colorPickerOptions.defaultColor,
      header_alignment: [null],
      logo_url: [null],
    });

    this.reportsService.reportLayout$
      .pipe(untilComponentDestroyed(this))
      .subscribe((reportLayout: ReportLayout<any>) => {
        // Get the contact
        if (reportLayout) {
          this.reportLayout = reportLayout;
          this.parsedDefinition = JSON.parse(reportLayout.definition);
          this.parsedFilters = JSON.parse(reportLayout.filters);
          this.setupFilters();

          //fill in v1 details with v2 details
          if (!this.parsedDefinition.pipeline.config.sort_by) {
            this.parsedDefinition.pipeline.config.sort_by = { field: 'updated_at', order: 'desc' };
            this.modifiedWithoutSave = true;
          }

          //fill in v2 details with v3 details
          if (!this.parsedDefinition.pipeline.config.sortings) {
            this.parsedDefinition.pipeline.config.sortings = [{ field: 'updated_at', order: 'desc' }];
            this.modifiedWithoutSave = true;
          }

          if (!this.parsedDefinition.pipeline.config.groupings) {
            this.parsedDefinition.pipeline.config.groupings = [];
            this.parsedDefinition.pipeline.config.groupings.push('project_stage_id');
            this.modifiedWithoutSave = true;
          }

          this.reportLayoutForm.patchValue(this.reportLayout);
        }
        // Mark for check
        this.changeDetectorRef.markForCheck();
      });

    if (!this.hasPermissionToEditForm) {
      this.disableAllForms();
    }
  }

  public onChangeColor(color: string): void {
    this.colorPickerOptions.selectedColor = color;
    this.renderer.setStyle(this.cpIcon._elementRef.nativeElement, 'color', color);
    this.reportLayoutForm.get('column_color').patchValue(color);
    this.changeDetectorRef.markForCheck();
  }

  public uploadCompanyLogo(photo: FileList): void {
    if (!photo.length) return;

    const allowedTypes = ['image/jpeg', 'image/png'];
    const file: File = photo[0];

    // Return if the file is not allowed
    if (!allowedTypes.includes(file.type)) return;

    this.reportLayoutForm.get('logo_url').patchValue(file);

    this.reportsService
      .uploadCompanyLogo(file, this.reportLayout.id)
      .pipe(
        untilComponentDestroyed(this),
        map((response) => response.report_layout),
      )
      .subscribe((res: ReportLayout<any>) => {
        if (res instanceof HttpErrorResponse) {
          this.userFeedbackMessage('Unable to upload logo at this moment. Try again later.');
        }
        this.reportLayout.logo_url = res.logo_url;
        this.userFeedbackMessage('Profile photo uploaded successfully.');
        this.changeDetectorRef.markForCheck();
      });
  }

  public removeCompanyLogo(): void {
    const companyLogoFormControl = this.reportLayoutForm.get('logo_url');
    companyLogoFormControl.setValue(null);
    this.logoFileInput.nativeElement.value = null;
    this.reportLayout.logo_url = null;

    this.reportsService
      .deleteCompanyLogo(this.reportLayout.id)
      .pipe(untilComponentDestroyed(this))
      .subscribe((res) => {
        if (res instanceof HttpErrorResponse) {
          this.userFeedbackMessage('Unable to delete logo at this moment. Try again later.');
        }
        this.userFeedbackMessage('Profile photo removed successfully.');
      });
  }

  lookupField(name) {
    if (name === '') {
      return {
        name: '',
        label: '---',
      };
    } else {
      let fields = this.fieldDefs.filter((fieldDef) => fieldDef.name == name);
      if (fields.length > 0) {
        return fields[0];
      } else {
        return null;
      }
    }
  }

  onFieldSelectionChange(event: MatSelectChange, colIndex: number, rowIndex: number): void {
    this.parsedDefinition.pipeline.fields[colIndex][rowIndex] = event.value;
    this.modifiedWithoutSave = true;
  }

  addPipelineField(): void {
    this.parsedDefinition.pipeline.fields.push(['id', 'id']);
    this.modifiedWithoutSave = true;
  }

  onSaveReport(): void {
    this.save();
  }

  addGrouping(): void {
    if (!this.parsedDefinition.pipeline.config.groupings) {
      this.parsedDefinition.pipeline.config.groupings = [];
    }
    this.parsedDefinition.pipeline.config.groupings.push('project_stage_id');
  }

  removeGrouping(index): void {
    this.parsedDefinition.pipeline.config.groupings.splice(index, 1);
    this.changeDetectorRef.markForCheck();
  }

  addSortBy(): void {
    if (!this.parsedDefinition.pipeline.config.sortings) {
      this.parsedDefinition.pipeline.config.sortings = [];
    }
    this.parsedDefinition.pipeline.config.sortings.push({ field: 'updated_at', order: 'desc' });
  }

  removeSorting(index): void {
    this.parsedDefinition.pipeline.config.sortings.splice(index, 1);
    this.changeDetectorRef.markForCheck();
  }

  duplicateReportLayout(): void {
    this.reportsService
      .duplicateReport(this.reportLayout)
      .pipe(untilComponentDestroyed(this))
      .subscribe((reportLayout) => {
        let route = this.activatedRoute;
        this.router.navigate(['../', reportLayout.id], { relativeTo: route });
        this.userFeedbackMessage('Report has been duplicated.');
      });
  }

  deleteReportLayout(): void {
    this.confirmService
      .openConfirmationModal()
      .pipe(filter((res: boolean) => res))
      .subscribe(() => {
        this.reportsService
          .deleteReport(this.reportLayout.id)
          .pipe(untilComponentDestroyed(this))
          .subscribe(() => {
            let route = this.activatedRoute;
            this.router.navigate(['../'], { relativeTo: route });
          });
      });
  }

  runReport(format: string = 'pdf'): void {
    //save the report first
    this.runningReport = true;
    this.changeDetectorRef.markForCheck();
    let sort = 'project_stage_rank';
    switch (this.parsedDefinition.pipeline.config.group_by) {
      case 'investment_type_id': {
        sort = 'investment_type_rank';
        break;
      }
      case 'property_type_id': {
        sort = 'property_type_name';
        break;
      }
      case 'project_stage_id': {
        sort = 'project_stage_rank';
        break;
      }
      case 'team_id': {
        sort = 'team_rank';
        break;
      }
      case 'vehicle_id': {
        sort = 'vehicles.rank';
        break;
      }
      default: {
        sort = 'project_stage_rank';
        break;
      }
    }

    const hasNestedFiltersOn = this.store.selectSnapshot(AccountState.isFeatureEnabled(FeatureFlag.NESTED_FILTERS));

    this.reportsService
      .runReport(
        this.reportLayout.id,
        // @ts-ignore
        this.parsedDefinition.pipeline.fields,
        this.parsedFilters,
        0,
        20,
        sort,
        'asc',
        format,
        this.parsedDefinition.pipeline.config.include_child_deals,
        this.parsedDefinition?.pipeline?.config?.sortings ?? [],
        hasNestedFiltersOn,
      )
      .pipe(untilComponentDestroyed(this))
      .subscribe((data) => {
        this.runningReport = false;
        this.changeDetectorRef.markForCheck();
        if (data && data.message) {
          this.userFeedbackMessage(data.message);
        } else if (data && [FileFormat.PDF, FileFormat.XLSX, FileFormat.CSV].includes(format as FileFormat)) {
          generateDownloadableFile(
            data,
            format as FileFormat,
            `${moment().format('MM.DD.YYYY')} ${this.reportLayout.name}`,
          );
        }
      });
  }

  save(): void {
    if (!this.hasPermissionToEditForm) return;

    this.modifiedWithoutSave = false;
    this.reportLayout.name = this.reportLayoutForm.get('name').value;
    this.reportLayout.description = this.reportLayoutForm.get('description').value;
    this.reportLayout.column_color = this.reportLayoutForm.get('column_color').value;
    this.reportLayout.header_alignment = this.reportLayoutForm.get('header_alignment').value;
    this.reportLayout.logo_url = this.reportLayoutForm.get('logo_url').value;
    this.reportLayout.definition = JSON.stringify(this.parsedDefinition);

    this.reportsService
      .updateReport(this.reportLayout)
      .pipe(untilComponentDestroyed(this))
      .subscribe(() => this.userFeedbackMessage('Report has been saved.'));
  }

  saveFilters(): void {
    if (!this.hasPermissionToEditForm) return;

    this.reportLayout.filters = JSON.stringify(this._internalFilterState);
    this.reportsService
      .updateReport(this.reportLayout)
      .pipe(untilComponentDestroyed(this))
      .subscribe(() => {
        this.userFeedbackMessage('Report filters has been saved.');
      });
  }

  private userFeedbackMessage(message: string): void {
    this.tsSnackbarService.open(message);
  }

  trackByFn(index: number, item: any): any {
    return item.id || index;
  }

  dropped({ previousContainer, container, currentIndex, previousIndex }: CdkDragDrop<any[]>): void {
    if (previousContainer === container) {
      moveItemInArray(container.data, previousIndex, currentIndex);
    } else {
      transferArrayItem(previousContainer.data, container.data, previousIndex, currentIndex);
      transferArrayItem(container.data, previousContainer.data, currentIndex + 1, previousIndex);
    }
    this.onSaveReport();
  }

  deleteReportColumn(columnIndex: number): void {
    this.parsedDefinition.pipeline.fields.splice(columnIndex, 1);
    this.modifiedWithoutSave = true;
  }

  onFilterRowsValidityChange(isValid: boolean): void {
    this.isFilterRowsValid = isValid;
  }

  filterChanged({ filters }: { filters: any[] }): void {
    this._internalFilterState = {
      fjson: FJSON(
        filters.map(({ id: name, filter: operator, value, alternate_value }) => ({
          name,
          operator,
          value: alternate_value ? [alternate_value] : value,
        })),
      ),
    };
  }

  setupFilters(): void {
    const filterKeys = Object.keys(this.parsedFilters || []);
    const newFilters = [];

    if (!filterKeys.find((key) => key === 'fjson')) {
      for (const key in this.parsedFilters) {
        if (['city', 'zip'].includes(key)) {
          continue;
        }

        const filterValue: string | number[] = this.parsedFilters[key];
        const filterName = camelToSnakeCase(key).split('_');
        const filterOperator = filterName.splice(filterName.length - 1, 1).join('');

        if (!filterValue) {
          continue;
        }

        newFilters.push({
          name: filterName.join('_'),
          operator: filterOperator.endsWith('q') ? filterOperator.substring(0, 3) : filterOperator,
          value: filterValue,
        });
      }

      this.parsedFilters = { fjson: FJSON(newFilters) };
    }
  }

  canShowThreeDotMenu(): boolean {
    if (this.store.selectSnapshot(AccountState.isFeatureEnabled(FeatureFlag.NEW_PERMISSIONS))) {
      return (
        this.store.selectSnapshot(UserState.hasPermission(NewPermission.PDF_REPORTS_CREATE)) ||
        this.store.selectSnapshot(UserState.hasPermission(NewPermission.PDF_REPORTS_DELETE))
      );
    }

    return true;
  }

  scheduleChanged(model: ReportLayout<any>): void {
    this.reportLayout = { ...this.reportLayout, ...model };

    this.reportsService
      .updateReport(this.reportLayout)
      .pipe(untilComponentDestroyed(this))
      .subscribe(() => this.userFeedbackMessage('Report has been saved.'));
  }

  private disableAllForms() {
    this.reportLayoutForm.disable();
  }
}
